/**
 * Task-comment queries: a question asked on a task that moves
 * open → answered (assignee replies) → closed (asker resolves).
 *
 * Shared by the daily / weekly / monthly review views, the Business Review
 * dashboard and My Dashboard, so every place shows queries the same way.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../../lib/api';
import { useWorkspace } from '../../../lib/workspace';
import Modal from '../../Modal';

export const QUERY_META = {
  open:     { label: 'Open',     color: '#f59e0b' },
  answered: { label: 'Answered', color: '#3b82f6' },
  closed:   { label: 'Closed',   color: '#22c55e' },
};

const REVIEW_TYPE_LABEL = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', other: 'Review' };

// Strip mention / attachment markers and HTML so a comment reads as plain text
export function queryText(body) {
  return String(body || '')
    .replace(/@\[\d+:([^\]]*)\]/g, '@$1')
    .replace(/\[\[(?:new|id):\d+\]\]/g, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function shortDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function ageDays(iso) {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function askedInLabel(q) {
  if (!q.review_type) return null;
  return `${REVIEW_TYPE_LABEL[q.review_type] || 'Review'} ${shortDate(q.review_date)}`;
}

/** Small "❓ Query · Open" pill shown on a query comment. */
export function QueryBadge({ status }) {
  const meta = QUERY_META[status] || QUERY_META.open;
  // Inline styles: also rendered on the task page, which doesn't load BusinessReviews.css
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, fontWeight: 600, lineHeight: 1,
      padding: '3px 7px', borderRadius: 10, whiteSpace: 'nowrap',
      color: meta.color, background: `${meta.color}22`,
    }}>❓ Query · {meta.label}</span>
  );
}

/** "Ask as query" tick-box placed beside a comment input. */
export function AskAsQueryToggle({ checked, onChange, disabled }) {
  return (
    <label className="brq-ask-toggle" title="The assignee is asked to reply; it stays pending until you resolve it">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />
      Ask as query
    </label>
  );
}

/** Chip for a person's stats row — renders nothing when there are no pending queries. */
export function PendingQueriesChip({ queries, open, onToggle }) {
  if (!queries?.length) return null;
  const answered = queries.filter(q => q.query_status === 'answered').length;
  return (
    <button
      type="button"
      className={'brq-chip' + (answered ? ' brq-chip--answered' : '') + (open ? ' brq-chip--open' : '')}
      onClick={e => { e.stopPropagation(); onToggle(); }}
      title={answered ? `${answered} answered — waiting for you to resolve` : 'Waiting for a reply'}
    >
      ❓ {queries.length} {queries.length === 1 ? 'query' : 'queries'}
      {answered > 0 && <span className="brq-chip-dot" />}
    </button>
  );
}

/** Expanded list of a person's pending queries (answered first). */
export function PendingQueriesList({ queries, onOpen }) {
  if (!queries?.length) return null;
  const sorted = [...queries].sort((a, b) =>
    (a.query_status === 'answered' ? 0 : 1) - (b.query_status === 'answered' ? 0 : 1));
  return (
    <div className="brq-pending" onClick={e => e.stopPropagation()}>
      <div className="brq-pending-title">Pending queries</div>
      {sorted.map(q => <QueryRow key={q.id} q={q} perspective="review" onOpen={onOpen} />)}
    </div>
  );
}

function QueryRow({ q, perspective, onOpen }) {
  const meta = QUERY_META[q.query_status] || QUERY_META.open;
  const age = ageDays(q.created_at);
  const overdue = q.query_status === 'open' && age >= 1;
  const askedIn = askedInLabel(q);

  let headline;
  if (perspective === 'for_me') {
    headline = q.query_status === 'open'
      ? `${q.asked_by?.name || 'Someone'} asked you`
      : 'You replied · waiting for them to resolve';
  } else if (q.query_status === 'answered') {
    headline = `${q.assigned_to?.name || 'Assignee'} replied`;
  } else if (perspective === 'asked') {
    headline = `Waiting on ${q.assigned_to?.name || 'assignee'}`;
  } else {
    headline = 'Waiting for reply';
  }

  return (
    <div className="brq-row" onClick={() => onOpen(q)}>
      <span className="brq-dot" style={{ background: meta.color }} />
      <div className="brq-row-main">
        <div className="brq-row-head">
          <span className="brq-row-headline">{headline}</span>
          <span className="brq-row-task">
            · {q.task_number ? `#${q.task_number} ` : ''}{q.task_title}
            {q.task_status === 'done' && <span className="brq-done"> ✓ Done</span>}
          </span>
        </div>
        <div className="brq-row-q">“{queryText(q.body)}”</div>
        {q.query_status === 'answered' && q.reply_body && (
          <div className="brq-row-reply">↳ {queryText(q.reply_body)}</div>
        )}
      </div>
      <div className="brq-row-meta">
        {overdue && <span className="brq-overdue">{age}d</span>}
        {askedIn && <span className="brq-row-src">{askedIn}</span>}
        <span className="brq-arrow">→</span>
      </div>
    </div>
  );
}

/**
 * Popup for one query: the question and what followed it on the task, plus
 * the actions open to the viewer — reply (assignee), or resolve / ask a
 * follow-up / reopen the task (the asker).
 */
export function QueryModal({ appId, query, onClose, onChanged }) {
  const api = useApi();
  const navigate = useNavigate();
  const { me, myRole } = useWorkspace();
  const [task, setTask]         = useState(null);
  const [comments, setComments] = useState([]);
  const [current, setCurrent]   = useState(query);
  const [text, setText]         = useState('');
  const [mode, setMode]         = useState(null);   // 'reply' | 'follow_up' | null
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');

  const load = useCallback(() => {
    Promise.all([
      api.getTask(appId, query.task_id).catch(() => null),
      api.listComments(appId, query.task_id).catch(() => ({ items: [] })),
    ]).then(([t, c]) => {
      setTask(t?.task || t);
      const items = c.items || [];
      setComments(items);
      const fresh = items.find(x => x.id === query.id);
      if (fresh) setCurrent(cur => ({ ...cur, ...fresh }));
    });
  }, [api, appId, query.task_id, query.id]);

  useEffect(() => { load(); }, [load]);

  const isAsker    = current.author_user_id === me?.id;
  const isAdmin    = myRole === 'admin' || myRole === 'super_admin';
  const isAssignee = (task?.assigned_to_user_id ?? current.assigned_to_user_id) === me?.id;
  const canResolve = isAsker || isAdmin;
  const canReopenTask = (task?.created_by_user_id === me?.id) && task?.status === 'done';
  const status     = current.query_status;

  const nameFor = (uid) => {
    if (uid === me?.id) return 'You';
    if (uid === current.author_user_id) return current.asked_by?.name || 'Asker';
    if (uid === (task?.assigned_to_user_id ?? current.assigned_to_user_id)) return current.assigned_to?.name || 'Assignee';
    return 'Team member';
  };

  // The query and everything said on the task after it
  const thread = comments.filter(c => new Date(c.created_at) >= new Date(current.created_at));

  async function run(fn) {
    setBusy(true); setError('');
    try {
      await fn();
      setText(''); setMode(null);
      load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  const resolve   = () => run(() => api.setQueryState(appId, current.task_id, current.id, 'resolve'));
  const reopenQ   = () => run(() => api.setQueryState(appId, current.task_id, current.id, 'reopen'));
  const reopenTask = () => run(() => api.updateTask(appId, current.task_id, { status: 'in_progress' }));
  const send = () => {
    const body = text.trim();
    if (!body) return;
    if (mode === 'follow_up') {
      run(() => api.addComment(appId, current.task_id, {
        body, is_query: true, follow_up_of: current.id, business_review_id: current.business_review_id || undefined,
      }));
    } else {
      run(() => api.addComment(appId, current.task_id, { body }));
    }
  };

  const askedIn = askedInLabel(current);

  return (
    <Modal
      open
      size="lg"
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {current.task_number ? `#${current.task_number} ` : ''}{current.task_title || task?.title}
          <QueryBadge status={status} />
        </span>
      }
      onClose={onClose}
    >
      <div className="brq-modal">
        <div className="brq-modal-sub">
          Asked by {nameFor(current.author_user_id)} · {shortDate(current.created_at)}
          {askedIn && <> · in {askedIn}</>}
          {task?.status && <> · Task: {task.status.replace(/_/g, ' ')}</>}
        </div>

        <div className="brq-thread">
          {(thread.length ? thread : [current]).map(c => (
            <div key={c.id} className={'brq-msg' + (c.id === current.id ? ' brq-msg--query' : '') + (c.is_query && c.id !== current.id ? ' brq-msg--followup' : '')}>
              <div className="brq-msg-meta">
                <strong>{nameFor(c.author_user_id)}</strong>
                <span>{shortDate(c.created_at)}</span>
                {c.is_query && <QueryBadge status={c.query_status} />}
              </div>
              <div className="brq-msg-body">{queryText(c.body) || 'Attachment added'}</div>
            </div>
          ))}
        </div>

        {error && <div className="brq-error">{error}</div>}

        {mode && (
          <div className="brq-compose">
            <textarea
              className="br-textarea"
              rows={3}
              autoFocus
              placeholder={mode === 'follow_up' ? 'Ask your follow-up question…' : 'Type your reply…'}
              value={text}
              onChange={e => setText(e.target.value)}
            />
            <div className="brq-actions">
              <button type="button" className="br-btn br-btn-secondary br-btn-sm" onClick={() => { setMode(null); setText(''); }} disabled={busy}>Cancel</button>
              <button type="button" className="br-btn br-btn-primary br-btn-sm" onClick={send} disabled={busy || !text.trim()}>
                {busy ? 'Sending…' : mode === 'follow_up' ? 'Ask follow-up' : 'Send reply'}
              </button>
            </div>
          </div>
        )}

        {!mode && (
          <div className="brq-actions">
            <button type="button" className="br-btn br-btn-secondary br-btn-sm" onClick={() => navigate(`/apps/${appId}/tasks/${current.task_id}`)}>
              Open task
            </button>
            <span style={{ flex: 1 }} />
            {isAssignee && status !== 'closed' && (
              <button type="button" className="br-btn br-btn-primary br-btn-sm" onClick={() => setMode('reply')} disabled={busy}>Reply</button>
            )}
            {canResolve && status !== 'closed' && (
              <>
                {canReopenTask && (
                  <button type="button" className="br-btn br-btn-secondary br-btn-sm" onClick={reopenTask} disabled={busy}>Reopen task</button>
                )}
                {isAsker && (
                  <button type="button" className="br-btn br-btn-secondary br-btn-sm" onClick={() => setMode('follow_up')} disabled={busy}>Ask follow-up</button>
                )}
                <button type="button" className="br-btn br-btn-primary br-btn-sm" onClick={resolve} disabled={busy}>Resolve</button>
              </>
            )}
            {canResolve && status === 'closed' && (
              <button type="button" className="br-btn br-btn-secondary br-btn-sm" onClick={reopenQ} disabled={busy}>Reopen query</button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

const SCOPE_TAB_LABEL = { for_me: 'For me', asked: 'Asked by me' };

/**
 * Dashboard panel with query tabs plus the existing calendar as the last tab.
 * Opens on the first query tab with something pending, else on the calendar,
 * so with no queries the panel looks the way it always did.
 *
 *   scopes        ['asked'] | ['for_me', 'asked']
 *   title         heading shown on the calendar tab
 *   calendar      the calendar element
 *   calendarLegend legend shown in the header while the calendar tab is active
 *   month         'YYYY-MM' — scopes the "tasks created" count
 *   onLoaded      ({ asked, for_me }) => void, with each scope's response
 */
export function QueriesPanel({ appId, scopes, title, calendar, calendarLegend, month, onLoaded, className, style, headClassName, titleClassName }) {
  const api = useApi();
  const [data, setData]       = useState({});
  const [tab, setTab]         = useState(null);
  const [openQuery, setOpenQuery] = useState(null);
  const scopeKey = scopes.join(',');

  const load = useCallback(() => {
    Promise.all(scopes.map(scope =>
      api.listReviewQueries(appId, { scope, status: 'pending', ...(month ? { month } : {}) })
        .then(r => [scope, r]).catch(() => [scope, { items: [], counts: { open: 0, answered: 0, closed: 0 } }])
    )).then(entries => {
      const next = Object.fromEntries(entries);
      setData(next);
      onLoaded?.(next);
      setTab(cur => cur || scopes.find(s => next[s]?.items?.length) || 'calendar');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, appId, scopeKey, month]);

  useEffect(() => { load(); }, [load]);

  const active = tab || 'calendar';
  const items  = data[active]?.items || [];
  const counts = data[active]?.counts;
  const onlyAsked = scopes.length === 1;
  // A query tab only appears while it has something pending (or is open),
  // so with nothing pending the header is just the calendar title.
  const visibleScopes = scopes.filter(s => data[s]?.items?.length || s === active);

  if (!visibleScopes.length) {
    return (
      <div className={className} style={style}>
        <div className={headClassName}>
          <span className={titleClassName}>{title}</span>
          {calendarLegend}
        </div>
        {calendar}
      </div>
    );
  }

  return (
    <div className={className} style={style}>
      <div className={headClassName}>
        <div className="brq-tabs">
          {visibleScopes.map(s => {
            const n = data[s]?.items?.length || 0;
            return (
              <button key={s} type="button" className={'brq-tab' + (active === s ? ' brq-tab--active' : '')} onClick={() => setTab(s)}>
                {onlyAsked ? 'Queries' : SCOPE_TAB_LABEL[s]}
                {n > 0 && <span className="brq-tab-count">{n}</span>}
              </button>
            );
          })}
          <button type="button" className={'brq-tab' + (active === 'calendar' ? ' brq-tab--active' : '')} onClick={() => setTab('calendar')}>
            <span className={titleClassName}>{title}</span>
          </button>
        </div>
        {active === 'calendar' ? calendarLegend : counts && (
          <div className="brq-counts">
            <span className="brq-count" style={{ '--q-color': QUERY_META.open.color }} title="Waiting for a reply">● {counts.open} open</span>
            <span className="brq-count" style={{ '--q-color': QUERY_META.answered.color }} title={active === 'for_me' ? 'You replied' : 'Replied — resolve them'}>● {counts.answered} answered</span>
          </div>
        )}
      </div>

      {active === 'calendar' ? calendar : (
        items.length === 0 ? (
          <div className="brq-empty">
            {active === 'for_me' ? 'No questions waiting for you.' : 'No pending queries. Tick “Ask as query” when commenting on a task in a review.'}
          </div>
        ) : (
          <div className="brq-list">
            {[...items]
              .sort((a, b) => {
                // what needs *this* viewer's action first
                const need = q => (active === 'for_me' ? q.query_status === 'open' : q.query_status === 'answered') ? 0 : 1;
                return need(a) - need(b);
              })
              .map(q => <QueryRow key={q.id} q={q} perspective={active} onOpen={setOpenQuery} />)}
          </div>
        )
      )}

      {openQuery && (
        <QueryModal appId={appId} query={openQuery} onClose={() => setOpenQuery(null)} onChanged={load} />
      )}
    </div>
  );
}
